import React, { useState } from "react";
import * as XLSX from "xlsx";
import CustomButton from "../Buttons/Button";
import useCurrency from "../../hooks/useCurrency";
import { getCurrencySymbol } from "../../Helper/getCurrencySymbol";
import { useDispatch, useSelector } from "react-redux";
import { downloadOnMobileUsersList } from "../../store/actions/userManagement/downloadOnMobileUsersList";

const ExportExcel = ({ columns, type, rows }) => {
  const [_currency, getAmount] = useCurrency();
  const { userFilter } = useSelector((state) => state?.dashboard);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const exportData = (columns, type, rows) => {
    if (!rows || !columns) return;

    const wb = XLSX.utils.book_new();

    if (type === "usersInvested") {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          rows?.map((user) => ({
            User_Id: user?.id,
            "Full Name": `${user?.firstName} ${user?.lastName}`,
            Email: user?.email,
            "Amount Invested": user?.amount,
            Date: user?.created_at,
          }))
        )
      );
      XLSX.writeFileXLSX(wb, "Data.xlsx");
    } else if (type === "walletToWallet") {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          rows?.map((row) => ({
            User_Id: row?.user_id,
            "First Name": row?.user_name?.split(" ")[0],
            "Last Name": row?.user_name?.split(" ")[1],
            Email: row?.user_email,
            "Base Amount ($/#)": `${getCurrencySymbol(row?.from_currency)}${
              row?.original_amount
            }`,
            "Exchange Rate (#)": `${getCurrencySymbol("NGR")}${
              row?.exchange_rate || 0
            }`,
            "Amount Converted ($/#)": `${getCurrencySymbol(row?.to_currency)}${
              row?.converted_amount
            }`,
            "Transfer Date": row?.conversion_date,
          }))
        )
      );
      XLSX.writeFileXLSX(wb, "Data.xlsx");
    } else if (type === "usersIntrested") {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          rows?.map((user) => ({
            User_Id: user?.id,
            "Full Name": `${user?.firstName} ${user?.lastName}`,
            Email: user?.email,
            "Amount Booked": user?.amount,
            Status: user?.status,
            Date: user?.created_at,
          }))
        )
      );
      XLSX.writeFileXLSX(wb, "Data.xlsx");
    } else {
      const sheetData = [
        columns?.map((col) => col?.headerName),
        ...rows.map((row) =>
          type === "bip"
            ? [row?.id, row?.email]
            : type === "users-main"
            ? [
                row?.id,
                row?.name?.split(" ")[0],
                row?.name?.split(" ")[1],
                row?.email,
                row?.phone,
                row?.profile?.BVN ? "YES" : "NO",
                new Date(row?.account_creation_date).toDateString(),
                new Date(row?.updated_at).toDateString(),
                row?.has_migrated ? "Yes" : "No",
                row?.status,
              ]
            : type === "users"
            ? [
                row?.id,
                row?.name?.split(" ")[0],
                row?.name?.split(" ")[1],
                row?.email,
                row?.phone,
                row?.profile?.BVN ? "YES" : "NO",
                new Date(row?.account_creation_date).toDateString(),

                row?.selectedPlan === "free"
                  ? "Non-member"
                  : row?.selectedPlan === null ||
                    row?.investorProfile === null ||
                    row?.hasProfileCompleted === false
                  ? "Incomplete"
                  : "Member",
                row?.free_approved_plan === "approved" &&
                row?.selectedPlan === "free"
                  ? "Approved"
                  : row?.free_approved_plan !== "approved"
                  ? "Pending"
                  : row?.selectedPlan !== "free" && row?.selectedPlan !== null
                  ? "Subscribed"
                  : null,
              ]
            : type === "account summary"
            ? [
                row?.email,
                row?.name?.split(" ")[0],
                row?.name?.split(" ")[1],
                row?.id,
                row?.wallet && _currency === "₦"
                  ? `₦${getAmount(row?.wallet?.walletBalance, 2)}`
                  : row?.wallet && _currency === "$"
                  ? `$${getAmount(row?.wallet?.dollarBalance, 2)}`
                  : "",
                row?.wallet?.accountName,
                row?.wallet?.accountNumber,

                row?.wallet?.bankName,
              ]
            : type === "account summary detail"
            ? [
                row?.transactionID,
                row?.narration,
                row?.debit,
                row?.credit,
                row?.walletBalance,
                row?.date,
              ]
            : type === "investment"
            ? [
                row?.id,
                row?.name,
                getAmount(Number(row?.total_amount_invested, 2)),
                getAmount(
                  Number(
                    row?.total_units && row?.amount_per_unit
                      ? row?.total_units * row?.amount_per_unit
                      : row?.maximum_amount,
                    2
                  )
                ),
                row?.category?.name || row?.investment_category?.name,
                row?.investors?.length || row?.investments?.length,
                row?.campaign_end_date,
                row?.status == "1"
                  ? "Active"
                  : row?.status == "7"
                  ? "Upcoming"
                  : "Closed",
              ]
            : type === "withdrawal requests"
            ? [
                row?.id,
                row?.user?.name?.split(" ")[0],
                row?.user?.name?.split(" ")[1],
                row?.user?.email,
                `${getCurrencySymbol(row?.currency)} ${getAmount(
                  row?.amount,
                  2
                )}`,
                new Date(row?.withdrawal_date)?.toDateString(),
                row?.status === "pending"
                  ? "Pending"
                  : row?.status === "cancelled"
                  ? "Cancelled"
                  : row?.status === "approved"
                  ? "Approved"
                  : "Processing",
              ]
            : type === "transaction"
            ? [
                row?.transaction_id,
                row?.description,
                row?.transaction_side,
                row?.transaction_type,
                `${getCurrencySymbol(row?.currency)} ${getAmount(
                  row?.amount,
                  2
                )}`,
                new Date(row?.transaction_date)?.toDateString(),
                row?.status,
              ]
            : type === "providus"
            ? [
                row?.userId,
                row?.name,
                row?.accountNumber,
                row?.sessionId,
                row?.amount,
                row?.date,
              ]
            : columns?.map((col) => "--")
        ),
      ];

      const sheet = XLSX.utils.aoa_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(wb, sheet);
      XLSX.writeFile(wb, "Data.xlsx");
    }
  };

  const downloadOnMobileData = async () => {
    setLoading(true);
    dispatch(
      downloadOnMobileUsersList({
        callback: (response) => {
          setLoading(false);
          if (response?.download_url) {
            window.open(response?.download_url, "_blank");
          }
        },
        failed: () => {
          setLoading(false);
        },
      })
    );
  };

  return (
    <div
      className="flex items-center border py-2 px-4 rounded-lg cursor-pointer"
      onClick={
        type === "users-main" && userFilter === 9
          ? downloadOnMobileData
          : () => exportData(columns, type, rows)
      }
    >
      {loading ? (
        <></>
      ) : (
        <img
          src="/Assets/cloud.png"
          alt=""
          className="object-contain w-4 h-6 mr-2"
        />
      )}
      {loading ? (
        <span className="font-[700] text-[12px] sm:text-sm">
          {/* Downloading ... */}
          DOWNLOADING ...
        </span>
      ) : (
        <span className="font-[500] text-[10px] sm:text-sm">
          Download Report
        </span>
      )}
    </div>
  );
};

export default ExportExcel;

// import React from 'react'
// import * as XLSX from 'xlsx';
// import CustomButton from '../Buttons/Button';

// const ExportExcel = ({rows}) => {

//     const exportData = () => {
//         const wb = XLSX.utils.book_new();
//         XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows));
//         XLSX.writeFileXLSX(wb, "Data.xlsx");
//       }

//   return (
//     <div className="flex items-center border p-2 rounded-lg cursor-pointer" onClick={exportData}>
//     <img src="/Assets/cloud.png" alt="" className="w-4 h-6 mr-2" />
//     <span className="font-[600] text-[10px] sm:text-base">Download Report</span>
//   </div>
//   )
// }
